import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import {
  ArrowBack,
  Phone,
  PhoneCallback,
  PhoneMissed,
  AccessTime,
  Notifications,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import * as Icons from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { ReportDateRangeFilter } from "components/CustomComponents/ReportDateRangeFilter";
import { FilterProvider } from "pages/FilterContext";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { themes, stickyHeader, tableRowSize, scrollTop } from "services/constants";
import { setFilterApi } from "services/Redux/userManagament";

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const avatarColor = (name = "") => {
  const colors = ["#84DEB8", "#6BBFDA", "#F4A261", "#E76F51", "#8ECAE6", "#9B72CF"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// ── CustomFilter headers — aligned with timeline filter signatures ───────────
const dailyReportFilterHeaders = [
  { key: "user_name",           name: "Employee Name",   type: "string",       icon: <Icons.PersonOutline /> },
  { key: "employee_code",       name: "Employee Code",   type: "autocomplete", icon: <Icons.Badge /> },
  { key: "total_calls",         name: "Total Calls",     type: "autocomplete", icon: <Icons.Phone /> },
  { key: "attended_calls",      name: "Attended",        type: "autocomplete", icon: <Icons.PhoneCallback /> },
  { key: "not_attended_calls",  name: "Not Attended",    type: "autocomplete", icon: <Icons.PhoneMissed /> },
  { key: "total_reminders",     name: "Total Reminders", type: "autocomplete", icon: <Icons.Notifications /> },
  { key: "follow_up_reminders", name: "Follow Up",       type: "autocomplete", icon: <Icons.Notifications /> },
  { key: "meeting_reminders",   name: "Meeting",         type: "autocomplete", icon: <Icons.Notifications /> },
  { key: "call_reminders",      name: "Call Reminders",  type: "autocomplete", icon: <Icons.Notifications /> },
];

// ── Sub-Component: Row Expansion Call Details ────────────────────────────────
const UserCallDetailsRow = ({ userId, startDate, endDate, domain, columnsCount }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const detailUrl = `${domain}crm/call-report/detail/?user_id=${userId}&start_date=${startDate}&end_date=${endDate}&company_id=${requestCompanyId}`;
  const { result: details, loading, setLoad: reCallDetailsApi } = useFetch(detailUrl);

  useEffect(() => {
    reCallDetailsApi(true);
  }, [userId, startDate, endDate, reCallDetailsApi]);

  if (loading) {
    return (
      <Mui.TableRow>
        <Mui.TableCell colSpan={columnsCount} align="center" sx={{ py: 3, bgcolor: "#f9fafb" }}>
          <Mui.CircularProgress size={24} />
        </Mui.TableCell>
      </Mui.TableRow>
    );
  }

  const calls = details?.call_details || [];

  return (
    <Mui.TableRow>
      <Mui.TableCell colSpan={columnsCount} sx={{ p: 0, bgcolor: "#f9fafb" }}>
        <Mui.Collapse in={true} timeout="auto" unmountOnExit>
          <Mui.Box sx={{ margin: 2, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
            <Mui.Table size="small">
              <Mui.TableHead>
                <Mui.TableRow sx={{ bgcolor: "#fff" }}>
                      <Mui.TableCell sx={{ fontWeight: "bold" }}>Contact</Mui.TableCell>
                  <Mui.TableCell sx={{ fontWeight: "bold" }}>Account</Mui.TableCell>
                  <Mui.TableCell align="center" sx={{ fontWeight: "bold" }}>Direction</Mui.TableCell>
                  <Mui.TableCell align="center" sx={{ fontWeight: "bold" }}>Status</Mui.TableCell>
                  <Mui.TableCell align="center" sx={{ fontWeight: "bold" }}>Outcome</Mui.TableCell>
                  <Mui.TableCell align="center" sx={{ fontWeight: "bold" }}>Start Time</Mui.TableCell>
                  <Mui.TableCell align="center" sx={{ fontWeight: "bold" }}>End Time</Mui.TableCell>
                  <Mui.TableCell align="center" sx={{ fontWeight: "bold" }}>Duration</Mui.TableCell>
                  <Mui.TableCell sx={{ fontWeight: "bold" }}>Notes</Mui.TableCell>
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {calls.length === 0 ? (
                  <Mui.TableRow>
                    <Mui.TableCell colSpan={9} align="center" sx={{ py: 2, color: "text.secondary" }}>
                      No separate call entries tracked for this record slot.
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  calls.map((call) => (
                    <Mui.TableRow key={call.call_id} hover>
                      <Mui.TableCell>
                        <Mui.Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {call.contact_name || "—"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell>
                        <Mui.Typography variant="body2" color="text.secondary">
                          {call.contact_company || "—"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell align="center">
                        <Mui.Chip
                          label={call.direction}
                          size="small"
                          variant="outlined"
                          color={call.direction === "inbound" ? "secondary" : "primary"}
                        />
                      </Mui.TableCell>
                      <Mui.TableCell align="center">
                        <Mui.Typography variant="body2" color={call.status === "answered" || call.status === "Call completed" ? "success.main" : "error.main"}>
                          {call.status}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell align="center">
                        <Mui.Typography variant="body2">{call.outcome || "—"}</Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell align="center">
                        <Mui.Typography variant="body2">
                          {call.start_time ? moment(call.start_time).format("DD-MM-YYYY HH:mm:ss") : "—"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell align="center">
                        <Mui.Typography variant="body2">
                          {call.end_time ? moment(call.end_time).format("DD-MM-YYYY HH:mm:ss") : "—"}
                        </Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell align="center">
                        <Mui.Typography variant="body2">{call.duration}</Mui.Typography>
                      </Mui.TableCell>
                      <Mui.TableCell>
                        <Mui.Typography variant="caption" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                          {call.notes || "—"}
                        </Mui.Typography>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.Box>
        </Mui.Collapse>
      </Mui.TableCell>
    </Mui.TableRow>
  );
};

// ── Daily Report Component ────────────────────────────────────────────────────
const DailyReport = ({ onBack }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, userScreenAccess, token } = useSelector(
    (state) => state.tokenReducer
  );
  const dispatch = useDispatch();

  const crmReports = userScreenAccess?.module_details?.find(
    (mod) =>
      mod.module_name === "Reports" ||
      mod.module_name === "CRM" ||
      mod.module_name === "CRM Reports"
  );
  const subModuleId = crmReports?.submodules?.find(
    (sub) =>
      sub.sub_module_name === "Reports" ||
      sub.sub_module_name?.toLowerCase()?.includes("reports")
  )?.sub_module_id;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [trigger, setTrigger] = useState(true);
  const containerRef = useRef(null);

  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("user_name");

  const [reportDataDetails, setReportDataDetails] = useState([]);
  const [filteredDataForCustomFilter, setFilteredDataForCustomFilter] = useState([]);
  const [isFilterInitialized, setIsFilterInitialized] = useState(false);

  const [expandedUserId, setExpandedUserId] = useState(null);

  const reportUrl = `${domain}crm/call-report/?user_id=${userDetails?.id}&start_date=${startDate}&end_date=${endDate}&company_id=${requestCompanyId}`;
  const { result, setLoad: reCallReportApi, loading } = useFetch(reportUrl);

  const handleStartDate = useCallback(
    (selectedStartDate, selectedEndDate) => {
      setStartDate(moment(selectedStartDate, "DD/MM/YYYY").format("YYYY-MM-DD"));
      setEndDate(moment(selectedEndDate, "DD/MM/YYYY").format("YYYY-MM-DD"));
      if (!trigger) {
        setTimeout(() => reCallReportApi(true), 150);
      } else {
        setTrigger(false);
      }
    },
    [trigger, reCallReportApi]
  );

  useEffect(() => {
    dispatch(setFilterApi(true));
  }, [dispatch, loading]);

  useEffect(() => {
    reCallReportApi(true);
  }, [startDate, endDate, reCallReportApi]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const reportData = useMemo(() => {
    if (!result) return [];
    if (Array.isArray(result)) return result;
    if (result.data && Array.isArray(result.data)) return result.data;
    if (typeof result === "object" && result.user_id) return [result];
    return [];
  }, [result]);

  useEffect(() => {
    if (reportData.length > 0 && !isFilterInitialized) {
      setFilteredDataForCustomFilter(reportData);
      setReportDataDetails(reportData);
      setIsFilterInitialized(true);
    }
  }, [reportData, isFilterInitialized]);

  useEffect(() => {
    if (reportData.length > 0) {
      setIsFilterInitialized(false);
    }
  }, [reportData.length]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      if (!filteredData || filteredData.length === 0) {
        setReportDataDetails([]);
        setFilteredDataForCustomFilter([]);
        return;
      }
      const reconstructed = filteredData
        .map((flat) => reportData.find((r) => r.user_id === flat.user_id))
        .filter(Boolean);

      setReportDataDetails(reconstructed);
      setFilteredDataForCustomFilter(filteredData);

      if (page * rowsPerPage >= reconstructed.length) setPage(0);
    },
    [reportData, page, rowsPerPage]
  );

  const descendingComparator = (a, b, prop) => {
    let valueA = a[prop];
    let valueB = b[prop];

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  };

  const getComparator = (sortOrder, sortProp) =>
    sortOrder === "desc"
      ? (a, b) => descendingComparator(a, b, sortProp)
      : (a, b) => -descendingComparator(a, b, sortProp);

  const stableSort = (array, comparator) =>
    array
      .map((el, index) => [el, index])
      .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
      .map((el) => el[0]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const nextOrder = isAsc ? "desc" : "asc";
    setOrder(nextOrder);
    setOrderBy(property);

    const comparator = getComparator(nextOrder, property);
    
    setReportDataDetails((prev) => stableSort([...prev], comparator));
    setFilteredDataForCustomFilter((prev) => stableSort([...prev], comparator));
    setPage(0);
  };

  const getDetailedHeaders = useCallback(() => {
    return [
      { key: "Employee Name", name: "Employee Name" },
      { key: "Employee Code", name: "Employee Code" },
      { key: "Contact", name: "Contact" },
      { key: "Account", name: "Account" },
      { key: "Direction", name: "Direction" },
      { key: "Status", name: "Status" },
      { key: "Outcome", name: "Outcome" },
      { key: "Start Time", name: "Start Time" },
      { key: "End Time", name: "End Time" },
      { key: "Duration", name: "Duration" },
      { key: "Notes", name: "Notes" },
    ];
  }, []);

  const handleRequestDetailedData = useCallback(
    async (filteredData) => {
      if (!filteredData || filteredData.length === 0) return [];

      const requests = filteredData.map((row) => {
        const detailUrl = `${domain}crm/call-report/detail/?user_id=${row.user_id}&start_date=${startDate}&end_date=${endDate}&company_id=${requestCompanyId}`;
        return fetch(detailUrl, {
          method: "GET",
          headers: {
            "content-type": "application/json",
            Authorization: `token ${token}`,
          },
        })
          .then(async (response) => {
            const payload = await response.json();
            return { ok: response.ok, payload, row };
          })
          .catch(() => ({ ok: false, payload: null, row }));
      });

      const detailedRows = [];
      const responses = await Promise.all(requests);

      responses.forEach(({ ok, payload, row }) => {
        const calls = payload?.call_details || [];
        calls.forEach((call) => {
          detailedRows.push({
            "Employee Name": row.user_name || "",
            "Employee Code": row.employee_code || "",
            Contact: call.contact_name || "",
            Account: call.contact_company || "",
            Direction: call.direction || "",
            Status: call.status || "",
            Outcome: call.outcome || "",
            "Start Time": call.start_time
              ? moment(call.start_time).format("DD-MM-YYYY HH:mm:ss")
              : "",
            "End Time": call.end_time
              ? moment(call.end_time).format("DD-MM-YYYY HH:mm:ss")
              : "",
            Duration: call.duration || "",
            Notes: call.notes || "",
          });
        });
      });

      return detailedRows;
    },
    [domain, endDate, startDate, token, requestCompanyId],
  );

  const totals = useMemo(
    () =>
      reportData.reduce(
        (acc, r) => ({
          calls: acc.calls + (r.total_calls || 0),
          attended: acc.attended + (r.attended_calls || 0),
          missed: acc.missed + (r.not_attended_calls || 0),
          reminders: acc.reminders + (r.total_reminders || 0),
        }),
        { calls: 0, attended: 0, missed: 0, reminders: 0 }
      ),
    [reportData]
  );

  const cardData = [
    { id: 1, label: "Total Calls",     value: totals.calls,     color: "#6BBFDA", icon: Phone,         iconBgColor: "#E0F7FA", borderColor: "#6BBFDA" },
    { id: 2, label: "Attended",        value: totals.attended,  color: "#84DEB8", icon: PhoneCallback, iconBgColor: "#E8F5E9", borderColor: "#84DEB8" },
    { id: 3, label: "Not Attended",    value: totals.missed,    color: "#E76F51", icon: PhoneMissed,   iconBgColor: "#FFEBEE", borderColor: "#E76F51" },
    { id: 4, label: "Total Reminders", value: totals.reminders, color: "#9B72CF", icon: Notifications, iconBgColor: "#F3E5F5", borderColor: "#9B72CF" },
  ];

  const columns = [
    { id: "action",              label: "Action",          sortable: false },
    { id: "user_name",           label: "Employee Name",   sortable: true  },
    { id: "employee_code",       label: "Employee Code",   sortable: true  },
    { id: "total_calls",         label: "Total Calls",     sortable: true  },
    { id: "attended_calls",      label: "Attended",        sortable: true  },
    { id: "not_attended_calls",  label: "Not Attended",    sortable: true  },
    { id: "total_duration",      label: "Duration",        sortable: false },
    { id: "total_reminders",     label: "Total Reminders", sortable: true  },
    { id: "follow_up_reminders", label: "Follow Up",       sortable: true  },
    { id: "meeting_reminders",   label: "Meeting",         sortable: true  },
    { id: "call_reminders",      label: "Call Reminders",  sortable: true  },
  ];

  const handleToggleRow = (userId) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  return (
    <Mui.Box sx={{ pt: 2 }}>
      {/* ── 1. Header Title Section ── */}
      <Mui.Box sx={{ display: "flex", alignItems: "center", px: 3, pb: 1, gap: 1.5 }}>
        <Mui.IconButton
          size="small"
          onClick={onBack}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <ArrowBack fontSize="small" />
        </Mui.IconButton>
        <Mui.Box>
          <Mui.Typography variant="h5" sx={{ fontWeight: 600, color: "#1E2640" }}>
            Daily Report
          </Mui.Typography>
          <Mui.Typography variant="body2" color="text.secondary">
            View daily call &amp; reminder activity of your CRM team members
          </Mui.Typography>
        </Mui.Box>
      </Mui.Box>

      {/* ── 2. Master Filters Wrapper Block (Matches Timeline Alignment Perfectly) ── */}
      <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
        <Mui.Box 
          className="custom-filter-row"
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            px: 3, 
            py: 1.5,
            gap: 2,
            borderBottom: "none"
          }}
        >
          <Mui.Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              color: "text.secondary",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              fontSize: "0.75rem"
            }}
          >
            FILTERS
          </Mui.Typography>
          <Mui.Box sx={{ width: 280 }}>
            <ReportDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              handleStartDate={handleStartDate}
              showToday={true}
              triggerDate={true}
            />
          </Mui.Box>
        </Mui.Box>

        {/* ── 3. Summary Cards Block ── */}
        <Mui.Box sx={{ px: 3, pt: 2, pb: 0, mb: -6 }}>
          <Mui.Box
            sx={{
              display: "flex",
              gap: 2.5,
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          ></Mui.Box>
          <Mui.Box sx={{ display: "flex", flexDirection: "row", gap: 2.5, flexWrap: "wrap" }}>
            {cardData.map((item) => {
              const IconComponent = item.icon;
              return (
                <Mui.Card
                  key={item.id}
                  sx={{
                    flex: "0 0 190px", width: 190, height: 105,
                    bgcolor: "#fff", borderRadius: 1, boxShadow: "none",
                    border: `1px solid ${item.borderColor}`,
                  }}
                >
                  <Mui.CardContent sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%", "&:last-child": { pb: 2 } }}>
                    <Mui.Box
                      sx={{
                        width: 28, height: 28, bgcolor: item.iconBgColor,
                        border: `0.5px solid ${item.borderColor}`, borderRadius: 1,
                        display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5,
                      }}
                    >
                      <IconComponent style={{ color: item.color, fontSize: 14 }} />
                    </Mui.Box>
                    <Mui.Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary", mb: "auto" }}>
                      {item.label}
                    </Mui.Typography>
                    <Mui.Typography variant="h5" sx={{ fontWeight: 700, color: "#32325D" }}>
                      {item.value}
                    </Mui.Typography>
                  </Mui.CardContent>
                </Mui.Card>
              );
            })}
          </Mui.Box>
        </Mui.Box>

        {/* ── 4. Custom Filter Toolbar — Matches Timeline Layout ── */}
        <Mui.Box sx={{ px: 3}}>
          <CustomFilter
            key="timeline-custom-filter"
            title={"Timeline Record"}
            filterHeaders={dailyReportFilterHeaders}
            data={reportData}
            filteredData={filteredDataForCustomFilter}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={true}
            showDownloadOptions={true}
            onRequestDetailedData={handleRequestDetailedData}
            getDetailedHeaders={getDetailedHeaders}
            showtitle_count={true}
            showFilter={false}
            filters={null}
            hideCount={true}
          />
        </Mui.Box>
      </FilterProvider>

      {/* ── 4. Main Data Table ── */}
      {loading ? (
        <Loader info="Loading..." />
      ) : (
        <Mui.Paper
          sx={{
            mx: 3, mb: 3, display: "flex", flexDirection: "column",
            overflow: "hidden", boxShadow: "none", border: "1px solid",
            borderColor: "divider", borderRadius: 1,
          }}
        >
          <Mui.Grid sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Mui.TableContainer ref={containerRef} sx={{ width: "100%", maxHeight: 520, overflow: "auto" }}>
              <Mui.Table className="border" size="small" stickyHeader>
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={{ height: 40 }}>
                    {columns.map((col) => (
                      <Mui.TableCell
                        key={col.id}
                        align="center"
                        onClick={() => col.sortable && handleRequestSort(col.id)}
                        sx={{
                          color: themes.headLine,
                          backgroundColor: themes.primary || "#f5f5f5",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          whiteSpace: "nowrap",
                          cursor: col.sortable ? "pointer" : "default",
                        }}
                      >
                        {col.sortable ? (
                          <Mui.TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            sx={{
                              "&.MuiTableSortLabel-active": { color: "inherit" },
                              "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                            }}
                          >
                            {col.label}
                          </Mui.TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>

                <Mui.TableBody>
                  {reportDataDetails.length === 0 ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={columns.length} align="center" sx={{ py: 6, border: "1px solid #ddd" }}>
                        <Mui.Alert severity="info">
                          {reportData.length === 0
                            ? "No data available for the selected date range."
                            : "No matching data found for your filter or search criteria."}
                        </Mui.Alert>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    reportDataDetails
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const isExpanded = expandedUserId === row.user_id;
                        return (
                          <React.Fragment key={row.user_id}>
                            <Mui.TableRow hover sx={tableRowSize}>
                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.IconButton size="small" onClick={() => handleToggleRow(row.user_id)}>
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </Mui.IconButton>
                              </Mui.TableCell>

                              <Mui.TableCell sx={{ border: "1px solid #ddd" }}>
                                <Mui.Stack direction="row" alignItems="center" gap={1}>
                                  <Mui.Avatar
                                    src={row.image_url || undefined}
                                    sx={{
                                      width: 28, height: 28, fontSize: "0.65rem",
                                      backgroundColor: !row.image_url ? avatarColor(row.user_name || "") : undefined,
                                      color: "#fff",
                                    }}
                                  >
                                    {!row.image_url && getInitials(row.user_name || "")}
                                  </Mui.Avatar>
                                  <Mui.Typography variant="body2">{row.user_name}</Mui.Typography>
                                </Mui.Stack>
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Typography variant="body2" color="text.secondary">
                                  {row.employee_code ?? "—"}
                                </Mui.Typography>
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Chip
                                  label={row.total_calls ?? 0}
                                  size="small"
                                  sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600, borderRadius: 1 }}
                                />
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Chip
                                  label={row.attended_calls ?? 0}
                                  size="small"
                                  sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, borderRadius: 1 }}
                                />
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Chip
                                  label={row.not_attended_calls ?? 0}
                                  size="small"
                                  sx={{
                                    backgroundColor: (row.not_attended_calls || 0) > 0 ? "#fbe9e7" : "#f5f5f5",
                                    color: (row.not_attended_calls || 0) > 0 ? "#bf360c" : "#666",
                                    fontWeight: 600, borderRadius: 1,
                                  }}
                                />
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
                                  <AccessTime sx={{ fontSize: 13, color: "text.secondary" }} />
                                  <Mui.Typography variant="body2">{row.total_duration || "00:00:00"}</Mui.Typography>
                                </Mui.Stack>
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Chip
                                  label={row.total_reminders ?? 0}
                                  size="small"
                                  sx={{ backgroundColor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600, borderRadius: 1 }}
                                />
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Typography variant="body2">{row.follow_up_reminders ?? 0}</Mui.Typography>
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Typography variant="body2">{row.meeting_reminders ?? 0}</Mui.Typography>
                              </Mui.TableCell>

                              <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                                <Mui.Typography variant="body2">{row.call_reminders ?? 0}</Mui.Typography>
                              </Mui.TableCell>
                            </Mui.TableRow>

                            {isExpanded && (
                              <UserCallDetailsRow 
                                userId={row.user_id}
                                startDate={startDate}
                                endDate={endDate}
                                domain={domain}
                                columnsCount={columns.length}
                              />
                            )}
                          </React.Fragment>
                        );
                      })
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>

            <Mui.Box sx={{ mt: 1 }}>
              {reportDataDetails.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={reportDataDetails.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              )}
            </Mui.Box>
          </Mui.Grid>
        </Mui.Paper>
      )}
    </Mui.Box>
  );
};

// ── Reports landing list ──────────────────────────────────────────────────────
const ReportsList = ({ onSelect }) => {
  const REPORT_NAMES = [{ label: "Daily Report" }];
  return (
    <Mui.Box sx={{ ml: 2, mr: 4 }}>
      <Mui.TableContainer sx={{ mt: 4, width: "100%", maxHeight: 850 }}>
        <Mui.Table className="border">
          <Mui.TableHead
            sx={{ backgroundColor: "#84DEB8", position: "sticky", top: -1, zIndex: 10 }}
          >
            <Mui.TableRow>
              <Mui.TableCell sx={{ fontWeight: "bold" }}>Report Name</Mui.TableCell>
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {REPORT_NAMES.map((report, index) => (
              <Mui.TableRow
                key={index}
                onClick={() => onSelect(report.label)}
                sx={{ cursor: "pointer", "&:hover": { backgroundColor: "action.hover" } }}
              >
                <Mui.TableCell>
                  <Mui.Typography>{report.label}</Mui.Typography>
                </Mui.TableCell>
              </Mui.TableRow>
            ))}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
    </Mui.Box>
  );
};

// ── Main CRM Reports page (entry point) ──────────────────────────────────────
export const CrmReports = () => {
  const [activeReport, setActiveReport] = useState(null);
  if (activeReport === "Daily Report") {
    return <DailyReport onBack={() => setActiveReport(null)} />;
  }
  return <ReportsList onSelect={setActiveReport} />;
};

export default CrmReports;